import { useState, useEffect, useCallback } from 'react'
import { AnnouncementService } from '../services/announcements'
import { AuthService } from '../services/auth'
import type { Announcement, AnnouncementInsert } from '../services/announcements'
import { toLocalDateTimeString } from '../utils/datetimeLocal'

interface AnnouncementFormData {
  title: string
  content: string
  url: string
  published_at: string
  image?: File
}

const AnnouncementManager = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null)
  const [formData, setFormData] = useState<AnnouncementFormData>({
    title: '',
    content: '',
    url: '',
    published_at: toLocalDateTimeString(),
    image: undefined
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const loadAnnouncements = useCallback(async () => {
    try {
      setIsLoading(true)
      const announcementsData = await AnnouncementService.getAllAnnouncements()
      setAnnouncements(announcementsData)
    } catch (error) {
      console.error('Failed to load announcements:', error)
      setError('お知らせの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAnnouncements()
  }, [loadAnnouncements])

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      url: '',
      published_at: toLocalDateTimeString(),
      image: undefined
    })
    setEditingAnnouncement(null)
    setImagePreview(null)
    setError('')
  }

  const handleAddAnnouncement = () => {
    resetForm()
    setShowAddForm(true)
  }

  const handleEditAnnouncement = (announcement: Announcement) => {
    setFormData({
      title: announcement.title,
      content: announcement.content,
      url: announcement.url || '',
      published_at: toLocalDateTimeString(announcement.published_at),
      image: undefined
    })
    setEditingAnnouncement(announcement)

    // Set image preview if announcement has image
    if (announcement.image_path) {
      const imageUrl = AnnouncementService.getImageUrl(announcement.image_path)
      setImagePreview(imageUrl)
    }

    setShowAddForm(true)
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('画像ファイルを選択してください')
        return
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('ファイルサイズは5MB以下にしてください')
        return
      }

      setFormData(prev => ({ ...prev, image: file }))

      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: undefined }))
    setImagePreview(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      let imagePath: string | undefined

      // Upload new image if provided
      if (formData.image) {
        imagePath = await AnnouncementService.uploadImage(formData.image)
      } else if (editingAnnouncement && !formData.image) {
        // Keep existing image path if no new image uploaded
        imagePath = editingAnnouncement.image_path
      }

      const announcementData: AnnouncementInsert = {
        title: formData.title,
        content: formData.content,
        url: formData.url || undefined,
        published_at: new Date(formData.published_at).toISOString(),
        image_path: imagePath
      }

      if (editingAnnouncement) {
        // Delete old image if new one uploaded
        if (formData.image && editingAnnouncement.image_path) {
          try {
            await AnnouncementService.deleteImage(editingAnnouncement.image_path)
          } catch (deleteError) {
            console.warn('Failed to delete old image:', deleteError)
          }
        }

        // Update existing announcement
        await AnnouncementService.updateAnnouncement(editingAnnouncement.id, announcementData)

        await AuthService.logAdminAction('update_announcement', 'announcement', editingAnnouncement.id, {
          title: formData.title
        })
      } else {
        // Create new announcement
        const newAnnouncement = await AnnouncementService.createAnnouncement(announcementData)

        await AuthService.logAdminAction('create_announcement', 'announcement', newAnnouncement.id, {
          title: formData.title
        })
      }

      setShowAddForm(false)
      resetForm()
      await loadAnnouncements()
    } catch (err) {
      console.error('Failed to save announcement:', err)
      setError(err instanceof Error ? err.message : 'お知らせの保存に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (announcement: Announcement) => {
    if (!confirm(`「${announcement.title}」を削除しますか？この操作は取り消せません。`)) {
      return
    }

    try {
      setIsLoading(true)

      // Delete image if exists
      if (announcement.image_path) {
        try {
          await AnnouncementService.deleteImage(announcement.image_path)
        } catch (deleteError) {
          console.warn('Failed to delete image:', deleteError)
        }
      }

      await AnnouncementService.deleteAnnouncement(announcement.id)

      await AuthService.logAdminAction('delete_announcement', 'announcement', announcement.id, {
        title: announcement.title
      })

      await loadAnnouncements()
    } catch (err) {
      console.error('Failed to delete announcement:', err)
      setError(err instanceof Error ? err.message : 'お知らせの削除に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isPublished = (announcement: Announcement) => {
    return new Date(announcement.published_at) <= new Date()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">お知らせ管理</h2>
        <button
          onClick={handleAddAnnouncement}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          disabled={isLoading}
        >
          新しいお知らせを追加
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
          <button
            onClick={() => setError('')}
            className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
          >
            閉じる
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingAnnouncement ? 'お知らせを編集' : '新しいお知らせを作成'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                タイトル *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                内容 *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                画像
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-md border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                外部リンクURL
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                公開日時 *
              </label>
              <input
                type="datetime-local"
                value={formData.published_at}
                onChange={(e) => setFormData(prev => ({ ...prev, published_at: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
              >
                {isLoading ? '保存中...' : editingAnnouncement ? '更新' : '作成'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false)
                  resetForm()
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Announcements List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading && announcements.length === 0 ? (
          <div className="p-6 text-center text-gray-500">読み込み中...</div>
        ) : announcements.length === 0 ? (
          <div className="p-6 text-center text-gray-500">お知らせがありません</div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {announcements.map((announcement) => (
              <li key={announcement.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {announcement.title}
                      </h3>
                      {!isPublished(announcement) && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          未公開
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2 line-clamp-2">
                      {announcement.content}
                    </p>
                    {announcement.image_path && (
                      <img
                        src={AnnouncementService.getImageUrl(announcement.image_path)}
                        alt=""
                        className="w-16 h-16 object-cover rounded-md mb-2"
                      />
                    )}
                    {announcement.url && (
                      <p className="text-blue-600 text-sm mb-2">
                        <a href={announcement.url} target="_blank" rel="noopener noreferrer">
                          {announcement.url}
                        </a>
                      </p>
                    )}
                    <div className="text-sm text-gray-500">
                      公開日時: {formatDate(announcement.published_at)} |
                      作成日時: {formatDate(announcement.created_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEditAnnouncement(announcement)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(announcement)}
                      className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default AnnouncementManager